/**
 * useResponsive Composable
 * 
 * Implementing Responsive Pattern from PATTERNS_FROM_BASKET_BASE.md
 * for Landing Page Phase 7 Optimization following ADR-048
 * 
 * Provides reactive responsive breakpoint management with:
 * - Tailwind CSS breakpoint system integration
 * - Touch device detection
 * - Orientation change handling
 * - Screen size utilities
 * - Mobile-first optimization helpers
 * 
 * Features:
 * - Reactive breakpoint tracking with Vue refs
 * - Touch/mouse interaction detection
 * - Mobile navigation helpers
 * - Adaptive content sizing
 * - Performance-optimized resize handling
 */

import { ref, computed, onMounted, onUnmounted } from 'vue'

export function useResponsive() {
  // Reactive screen dimensions
  const screenWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024)
  const screenHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 768)
  const orientation = ref('landscape')
  
  // Touch capabilities
  const isTouchDevice = ref(false)
  const supportsHover = ref(true)
  
  // Tailwind CSS breakpoints (matching main.css configuration)
  const breakpoints = {
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    '2xl': 1536
  }

  /**
   * Computed breakpoint flags
   */
  const isXs = computed(() => screenWidth.value < breakpoints.sm)
  const isSm = computed(() => screenWidth.value >= breakpoints.sm && screenWidth.value < breakpoints.md)
  const isMd = computed(() => screenWidth.value >= breakpoints.md && screenWidth.value < breakpoints.lg)
  const isLg = computed(() => screenWidth.value >= breakpoints.lg && screenWidth.value < breakpoints.xl)
  const isXl = computed(() => screenWidth.value >= breakpoints.xl && screenWidth.value < breakpoints['2xl'])
  const is2xl = computed(() => screenWidth.value >= breakpoints['2xl'])
  
  // Convenience computed properties
  const isMobile = computed(() => screenWidth.value < breakpoints.md)
  const isTablet = computed(() => screenWidth.value >= breakpoints.md && screenWidth.value < breakpoints.lg)
  const isDesktop = computed(() => screenWidth.value >= breakpoints.lg)
  
  // Orientation
  const isLandscape = computed(() => orientation.value === 'landscape')
  const isPortrait = computed(() => orientation.value === 'portrait')
  
  // Device type detection
  const deviceType = computed(() => {
    if (isMobile.value) return 'mobile'
    if (isTablet.value) return 'tablet'
    return 'desktop'
  })

  /**
   * Update screen dimensions and orientation
   */
  const updateScreenInfo = () => {
    screenWidth.value = window.innerWidth
    screenHeight.value = window.innerHeight
    orientation.value = window.innerHeight > window.innerWidth ? 'portrait' : 'landscape'
  }

  /**
   * Debounced resize handler for performance
   */
  let resizeTimeout = null
  const handleResize = () => {
    clearTimeout(resizeTimeout)
    resizeTimeout = setTimeout(() => {
      updateScreenInfo()
    }, 100) // 100ms debounce
  }

  /**
   * Handle orientation change
   */
  const handleOrientationChange = () => {
    // Small delay to ensure dimensions are updated
    setTimeout(() => {
      updateScreenInfo()
    }, 100)
  }

  /**
   * Detect touch capabilities
   */
  const detectTouchCapabilities = () => {
    // Check for touch support
    isTouchDevice.value = (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0
    )
    
    // Check for hover support (desktop mice)
    supportsHover.value = window.matchMedia('(hover: hover)').matches
  }

  /**
   * Get current breakpoint name
   */
  const getCurrentBreakpoint = () => {
    const width = screenWidth.value
    
    if (width >= breakpoints['2xl']) return '2xl'
    if (width >= breakpoints.xl) return 'xl'
    if (width >= breakpoints.lg) return 'lg'
    if (width >= breakpoints.md) return 'md'
    if (width >= breakpoints.sm) return 'sm'
    return 'xs'
  }

  /**
   * Check if current breakpoint matches or is larger than specified
   */
  const isBreakpointOrLarger = (breakpoint) => {
    const current = getCurrentBreakpoint()
    const sizes = ['xs', 'sm', 'md', 'lg', 'xl', '2xl']
    const currentIndex = sizes.indexOf(current)
    const targetIndex = sizes.indexOf(breakpoint)
    
    return currentIndex >= targetIndex
  }

  /**
   * Get appropriate font sizes for current breakpoint
   */
  const getResponsiveFontSize = (baseSize = 'base') => {
    const fontSizes = {
      xs: {
        'xs': 'text-xs',
        'sm': 'text-sm',
        'base': 'text-base',
        'lg': 'text-lg',
        'xl': 'text-xl',
        '2xl': 'text-xl',
        '3xl': 'text-2xl',
        '4xl': 'text-2xl'
      },
      sm: {
        'xs': 'text-sm',
        'sm': 'text-base',
        'base': 'text-lg',
        'lg': 'text-xl',
        'xl': 'text-2xl',
        '2xl': 'text-2xl',
        '3xl': 'text-3xl',
        '4xl': 'text-3xl'
      },
      md: {
        'xs': 'text-sm',
        'sm': 'text-base',
        'base': 'text-lg',
        'lg': 'text-xl',
        'xl': 'text-2xl',
        '2xl': 'text-3xl',
        '3xl': 'text-4xl',
        '4xl': 'text-4xl'
      },
      lg: {
        'xs': 'text-base',
        'sm': 'text-lg',
        'base': 'text-xl',
        'lg': 'text-2xl',
        'xl': 'text-3xl',
        '2xl': 'text-4xl',
        '3xl': 'text-5xl',
        '4xl': 'text-6xl'
      }
    }
    
    const currentBreakpoint = getCurrentBreakpoint()
    const breakpointSizes = fontSizes[currentBreakpoint] || fontSizes.lg
    
    return breakpointSizes[baseSize] || breakpointSizes.base
  }

  /**
   * Get optimal grid columns for current breakpoint
   */
  const getGridColumns = (maxCols = 3) => {
    if (isXs.value) return 1
    if (isSm.value) return Math.min(2, maxCols)
    if (isMd.value) return Math.min(2, maxCols)
    return maxCols
  }

  /**
   * Get appropriate spacing for current breakpoint
   */
  const getResponsiveSpacing = (size = 'base') => {
    const spacings = {
      xs: {
        'sm': 'gap-2',
        'base': 'gap-4',
        'lg': 'gap-6',
        'xl': 'gap-8'
      },
      sm: {
        'sm': 'gap-3',
        'base': 'gap-6',
        'lg': 'gap-8',
        'xl': 'gap-10'
      },
      md: {
        'sm': 'gap-4',
        'base': 'gap-6',
        'lg': 'gap-8',
        'xl': 'gap-12'
      },
      lg: {
        'sm': 'gap-6',
        'base': 'gap-8',
        'lg': 'gap-12',
        'xl': 'gap-16'
      }
    }
    
    const currentBreakpoint = getCurrentBreakpoint()
    const breakpointSpacings = spacings[currentBreakpoint] || spacings.lg
    
    return breakpointSpacings[size] || breakpointSpacings.base
  }

  /**
   * Get mobile-optimized button classes
   */
  const getMobileButtonClasses = () => {
    return isMobile.value 
      ? 'btn-mobile-optimized touch-target-mobile' 
      : 'px-6 py-3'
  }

  /**
   * Get container classes based on screen size
   */
  const getContainerClasses = (type = 'default') => {
    const containers = {
      default: isMobile.value ? 'container-narrow' : 'container-legal',
      hero: isMobile.value ? 'container-hero px-4' : 'container-hero',
      section: isMobile.value ? 'container-section px-4' : 'container-section',
      wide: isMobile.value ? 'container-legal px-4' : 'container-wide'
    }
    
    return containers[type] || containers.default
  }

  /**
   * Check if element should use mobile layout
   */
  const shouldUseMobileLayout = (threshold = 'md') => {
    const thresholds = {
      sm: breakpoints.sm,
      md: breakpoints.md,
      lg: breakpoints.lg
    }
    
    return screenWidth.value < (thresholds[threshold] || breakpoints.md)
  }

  /**
   * Initialize responsive system
   */
  const initResponsive = () => {
    if (typeof window === 'undefined') return
    
    updateScreenInfo()
    detectTouchCapabilities()
    
    // Add event listeners
    window.addEventListener('resize', handleResize, { passive: true })
    window.addEventListener('orientationchange', handleOrientationChange, { passive: true })
    
    // Update CSS custom properties for breakpoints
    document.documentElement.style.setProperty('--screen-width', `${screenWidth.value}px`)
    document.documentElement.style.setProperty('--screen-height', `${screenHeight.value}px`)
    
    // Add device type classes
    document.documentElement.classList.add(`device-${deviceType.value}`)
    if (isTouchDevice.value) document.documentElement.classList.add('touch-device')
    if (!supportsHover.value) document.documentElement.classList.add('no-hover')
  }

  /**
   * Cleanup function
   */
  const cleanup = () => {
    if (typeof window === 'undefined') return
    
    window.removeEventListener('resize', handleResize)
    window.removeEventListener('orientationchange', handleOrientationChange)
    clearTimeout(resizeTimeout)
  }

  // Initialize on mount
  onMounted(() => {
    initResponsive()
  })

  // Cleanup on unmount
  onUnmounted(() => {
    cleanup()
  })

  return {
    // Screen dimensions
    screenWidth,
    screenHeight,
    orientation,
    
    // Breakpoint flags
    isXs,
    isSm,
    isMd,
    isLg,
    isXl,
    is2xl,
    
    // Convenience flags
    isMobile,
    isTablet,
    isDesktop,
    isLandscape,
    isPortrait,
    
    // Device capabilities
    isTouchDevice,
    supportsHover,
    deviceType,
    
    // Utility methods
    getCurrentBreakpoint,
    isBreakpointOrLarger,
    getResponsiveFontSize,
    getGridColumns,
    getResponsiveSpacing,
    getMobileButtonClasses,
    getContainerClasses,
    shouldUseMobileLayout,
    
    // Internal methods
    updateScreenInfo,
    cleanup
  }
}

/**
 * Utility function to get CSS classes for responsive text
 */
export function getResponsiveTextClasses(baseSize = 'base') {
  const sizes = {
    'xs': 'text-responsive-xs',
    'sm': 'text-responsive-sm', 
    'base': 'text-responsive-base',
    'lg': 'text-responsive-lg',
    'xl': 'text-responsive-xl',
    '2xl': 'text-responsive-2xl'
  }
  
  return sizes[baseSize] || sizes.base
}

/**
 * Utility function to check if device has good network connection
 */
export function hasGoodConnection() {
  const connection = navigator.connection
  if (!connection) return true // Assume good connection if not available
  
  const goodTypes = ['4g', '5g']
  return goodTypes.includes(connection.effectiveType)
}